/**
 * One of today's meetings (design: "Today's meetings", "One-tap pre-call text"):
 * - before it starts: the pre-call text, ready to send from the rep's phone;
 * - once it has started: Held, No-show or Rescheduled, one tap each. Held and no-show keep the
 *   calendar event (zInspector's sync marks a no-show cancelled); rescheduled moves it.
 */
import { CalendarClock, Check, MessageSquareText, UserX } from "lucide-react";
import { useNotify, useUpdate } from "ra-core";
import { useState } from "react";
import { Link } from "react-router";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

import type {
  MeetingOutcome,
  ZcrmActivity,
} from "../providers/zinspector/activities";
import {
  fillTemplate,
  meetingState,
  PRE_CALL_TEMPLATES,
  smsLink,
} from "./today";

export interface MeetingPerson {
  name: string;
  first: string;
  phone: string;
  account: string;
}

const formatTime = (iso: string | null) =>
  iso
    ? new Intl.DateTimeFormat("en-US", {
        hour: "numeric",
        minute: "2-digit",
      }).format(new Date(iso))
    : "";

const OUTCOME_TEXT = {
  held: "Held",
  no_show: "No-show",
} as const;

export const MeetingCard = ({
  meeting,
  person,
  hostName,
  brand,
  noShows,
  now,
}: {
  meeting: ZcrmActivity;
  person: MeetingPerson;
  hostName: string;
  brand: string;
  noShows: number;
  now?: Date;
}) => {
  const [update, { isPending }] = useUpdate<ZcrmActivity>();
  const notify = useNotify();
  const [dialog, setDialog] = useState<"text" | "reschedule" | null>(null);
  const state = meetingState(meeting, now);

  const save = (
    data: Partial<ZcrmActivity> & { outcome?: MeetingOutcome },
    message: string,
  ) =>
    update(
      "activities",
      { id: meeting.id, data, previousData: meeting },
      {
        mutationMode: "pessimistic",
        onSuccess: () => {
          notify(message, { type: "success" });
          setDialog(null);
        },
        onError: (error) =>
          notify(error instanceof Error ? error.message : "Saving failed", {
            type: "error",
          }),
      },
    );

  return (
    <article
      aria-label={`${formatTime(meeting.starts_at)} ${meeting.title}`}
      className={cn(
        "grid gap-x-4 gap-y-2 rounded-md border bg-card px-4 py-3 sm:grid-cols-[6rem_minmax(0,1fr)_auto]",
        state === "ask_outcome" && "border-primary",
      )}
    >
      <div>
        <strong className="block text-lg">
          {formatTime(meeting.starts_at)}
        </strong>
        <span className="text-xs text-muted-foreground">{meeting.kind}</span>
      </div>
      <div className="flex min-w-0 flex-col gap-1">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="font-semibold">{meeting.title}</span>
          {noShows ? (
            <Badge variant="error">
              {noShows} earlier no-show{noShows > 1 ? "s" : ""}
            </Badge>
          ) : null}
        </div>
        <span className="text-sm text-muted-foreground">
          {[person.name, person.account].filter(Boolean).join(" · ")}
          {meeting.company_id ? (
            <>
              {" · "}
              <Link
                to={`/companies/${meeting.company_id}/show?tab=calls`}
                className="underline-offset-2 hover:underline"
              >
                Account
              </Link>
            </>
          ) : null}
        </span>
        {state === "held" || state === "no_show" ? (
          <Badge variant={state === "held" ? "success" : "error"}>
            {OUTCOME_TEXT[state]}
          </Badge>
        ) : null}
      </div>
      <div className="flex flex-wrap items-start justify-end gap-1.5">
        {state === "upcoming" ? (
          <Button size="sm" onClick={() => setDialog("text")}>
            <MessageSquareText /> Pre-call text
          </Button>
        ) : null}
        {state === "ask_outcome" ? (
          <>
            <Button
              size="sm"
              disabled={isPending}
              className="bg-success text-white hover:bg-success/90"
              onClick={() =>
                save({ outcome: "held" }, "Held · logged on the account")
              }
            >
              <Check /> Held
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={isPending}
              className="border-destructive text-destructive hover:bg-error-soft"
              onClick={() => save({ outcome: "no_show" }, "No-show logged")}
            >
              <UserX /> No-show
            </Button>
          </>
        ) : null}
        {state === "upcoming" || state === "ask_outcome" ? (
          <Button
            size="sm"
            variant="outline"
            onClick={() => setDialog("reschedule")}
          >
            <CalendarClock /> Rescheduled
          </Button>
        ) : null}
      </div>
      {dialog === "text" ? (
        <PreCallDialog
          meeting={meeting}
          person={person}
          hostName={hostName}
          brand={brand}
          onClose={() => setDialog(null)}
        />
      ) : null}
      {dialog === "reschedule" ? (
        <RescheduleDialog
          meeting={meeting}
          isSaving={isPending}
          onSave={(startsAt, endsAt) =>
            save(
              { starts_at: startsAt, ends_at: endsAt },
              "Rescheduled · the calendar event moves too",
            )
          }
          onClose={() => setDialog(null)}
        />
      ) : null}
    </article>
  );
};

const PreCallDialog = ({
  meeting,
  person,
  hostName,
  brand,
  onClose,
}: {
  meeting: ZcrmActivity;
  person: MeetingPerson;
  hostName: string;
  brand: string;
  onClose: () => void;
}) => {
  const values = {
    first: person.first || "there",
    host: hostName.split(" ")[0] || hostName,
    company: brand,
    time: formatTime(meeting.starts_at),
    link: "[video link]",
  };
  const [text, setText] = useState(
    fillTemplate(PRE_CALL_TEMPLATES[0].text, values),
  );
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Pre-call text</DialogTitle>
          <DialogDescription>
            {person.phone
              ? `To ${person.name || "them"}, ${person.phone}. It opens your phone's messages, ready to send.`
              : "No mobile on file: copy the text, or add their mobile in zInspector."}
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap gap-1.5">
          {PRE_CALL_TEMPLATES.map((t) => (
            <Button
              key={t.id}
              type="button"
              size="sm"
              variant="outline"
              onClick={() => setText(fillTemplate(t.text, values))}
            >
              {t.label}
            </Button>
          ))}
        </div>
        <Textarea
          aria-label="Text"
          rows={4}
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => navigator.clipboard?.writeText(text)}
          >
            Copy
          </Button>
          {person.phone ? (
            <Button asChild>
              <a href={smsLink(person.phone, text)}>Send from my phone</a>
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const toLocalInput = (iso: string | null) => {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const RescheduleDialog = ({
  meeting,
  isSaving,
  onSave,
  onClose,
}: {
  meeting: ZcrmActivity;
  isSaving: boolean;
  onSave: (startsAt: string, endsAt: string) => void;
  onClose: () => void;
}) => {
  const [when, setWhen] = useState(toLocalInput(meeting.starts_at));
  const length =
    meeting.starts_at && meeting.ends_at
      ? Date.parse(meeting.ends_at) - Date.parse(meeting.starts_at)
      : 30 * 60_000;
  const start = when ? new Date(when) : null;
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>Reschedule</DialogTitle>
          <DialogDescription>
            The calendar event moves too, for everyone invited.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="reschedule-when">New time</Label>
          <Input
            id="reschedule-when"
            type="datetime-local"
            value={when}
            onChange={(e) => setWhen(e.target.value)}
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={!start || Number.isNaN(start.getTime()) || isSaving}
            onClick={() =>
              start &&
              onSave(
                start.toISOString(),
                new Date(start.getTime() + length).toISOString(),
              )
            }
          >
            Move the meeting
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
